/**
 * Convert a UTC date to the user's local timezone.
 * @param {string | Date} utcDate - The UTC date string or Date object.
 * @returns {string} - The formatted date in the user's local timezone (DD/MM/YYYY - HH:MM AM/PM).
 */
export const convertUTCToLocal = (utcDate) => {
  if (!utcDate) return '';
  const date = new Date(utcDate);

  const datePart = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const timePart = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  return `${datePart} - ${timePart.toUpperCase()}`;
};

/**
 * Convert a local date to UTC.
 * @param {string | Date} localDate - The local date string or Date object.
 * @param {string} [timeZone] - The local timezone (e.g., 'Africa/Cairo'). Defaults to the user's timezone.
 * @returns {string} - The UTC date in ISO format.
 */
/**
 * Convert a local date to UTC in ISO 8601 format.
 * @param {string | Date} localDate - The local date string or Date object.
 * @returns {string} - The UTC date in ISO 8601 format.
 */
export const convertLocalToUTC = (localDate) => {
  const date = new Date(localDate); // Parse the local date

  // Extract UTC components
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  const milliseconds = String(date.getUTCMilliseconds()).padStart(3, '0');

  // Construct the ISO 8601 string
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
};

/**
 * Parse date range from different possible formats
 * @param {any} dateRange - date range in various possible formats
 * @returns {object} - { startDate, endDate }
 */
export const parseDateRange = (dateRange) => {
  let startDate = null;
  let endDate = null;

  if (Array.isArray(dateRange)) {
    [startDate, endDate] = dateRange;
  } else if (dateRange?.startDate && dateRange?.endDate) {
    ({ startDate, endDate } = dateRange);
  } else if (dateRange?.start && dateRange?.end) {
    startDate = dateRange.start;
    endDate = dateRange.end;
  } else if (dateRange?.from && dateRange?.to) {
    startDate = dateRange.from;
    endDate = dateRange.to;
  }

  return { startDate, endDate };
};

/**
 * Check if a date falls within the given date range
 * @param {Date} date - date to check
 * @param {any} startDate - start date (can be Moment object or Date)
 * @param {any} endDate - end date (can be Moment object or Date)
 * @returns {boolean} - true if date is in range
 */
export const checkDateInRange = (date, startDate, endDate) => {
  let inRange = true;

  if (startDate) {
    // Handle Moment objects - convert to Date or use Moment methods
    const fromDate = startDate._isAMomentObject
      ? startDate.toDate()
      : new Date(startDate);
    fromDate.setHours(0, 0, 0, 0); // Start of day
    inRange = inRange && date >= fromDate;
  }
  if (endDate) {
    // Handle Moment objects - convert to Date or use Moment methods
    const toDate = endDate._isAMomentObject
      ? endDate.toDate()
      : new Date(endDate);
    toDate.setHours(23, 59, 59, 999); // End of day
    inRange = inRange && date <= toDate;
  }

  return inRange;
};

// export const convertLocalToUTC = (localDate) => {
//   const date = new Date(localDate); // Parse the local date
//   return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString(); // Convert to UTC
// };

// /**
//  * Format a date to a specific timezone.
//  * @param {string | Date} date - The date string or Date object.
//  * @param {string} timeZone - The target timezone (e.g., 'America/New_York').
//  * @returns {string} - The formatted date in the specified timezone.
//  */
// export const formatDateToTimeZone = (date, timeZone) => {
//   const dateObj = new Date(date); // Parse the date
//   // console.log("UTC date:", dateObj);
//   const newDate = dateObj.toLocaleString('en-GB', {
//     year: 'numeric',
//     month: '2-digit',
//     day: '2-digit',
//     hour: '2-digit',
//     minute: '2-digit',
//     second: '2-digit',
//     hour12: false, // Use 24-hour format
//     timeZone, // Target timezone
//   });
//   // console.log("Formatted date:", newDate);
//   return newDate; // Return the formatted date
// };
